import type { Translations } from '../types';

export const en: Translations = {
  nav: {
    items: [
      { title: 'About Us', id: 'about', isSection: true },
      { title: 'Our Services', id: 'services', isSection: true },
      { title: 'FAQ', id: 'faq', isSection: true },
      { title: 'Service Providers', id: 'service-provider', isSection: false },
    ],
    home: 'Home',
    vendorButton: 'Service Provider',
    customerButton: 'Customer',
    mobileVendorButton: 'Provider Login',
    mobileCustomerButton: 'Customer Login',
  },
  hero: {
    brandName: 'MedHub',
    title: 'Your Smart Platform for Field Medical Services',
    description:
      'Get quotes from licensed medical service providers, compare them, and choose the best fit for your project or event - all through one trusted platform',
    joinAsVendor: 'Join as Service Provider',
    registerAsCustomer: 'Join as Customer',
  },
  about: {
    title: 'Who Are We?',
    description:
      'MedHub is a Saudi digital platform connecting field medical service seekers with licensed providers, aiming to facilitate obtaining required medical coverage for projects and events with high efficiency and transparency. The platform works as a specialized marketplace bringing together companies and institutions needing field medical services with licensed clinics and medical centers capable of providing these services according to the highest quality and safety standards.',
    vision: {
      title: 'Our Vision',
      description:
        'To be the leading platform in the Kingdom connecting field medical service seekers with their providers',
      alt: 'Our Vision',
    },
    mission: {
      title: 'Our Mission',
      description:
        'Enabling companies to obtain reliable field medical services quickly and easily',
      alt: 'Our Mission',
    },
  },
  services: {
    title: 'Our Services',
    items: [
      {
        id: 'ambulances',
        title: 'Ambulances',
        description:
          'Fully equipped ambulances with qualified medical teams for presence at project sites and events',
      },
      {
        id: 'mobile-clinics',
        title: 'Mobile Clinics',
        description:
          'Mobile clinics equipped with the latest medical equipment to provide basic health services',
      },
      {
        id: 'medical-teams',
        title: 'Field Medical Teams',
        description:
          'Complete medical teams including doctors, nurses, and paramedics for field presence',
      },
      {
        id: 'medical-equipment',
        title: 'Medical Equipment',
        description:
          'Providing medical and emergency equipment and supplies according to project requirements',
      },
    ],
  },
  whyChooseUs: {
    title: 'Why MedHub?',
    features: {
      transparentComparison: {
        title: 'Transparent Comparison',
        description:
          'Compare offers based on price, specifications, and previous ratings',
      },
      fastOffers: {
        title: 'Fast Offers',
        description:
          'Get multiple quotes from qualified service providers in record time',
      },
      secureContracts: {
        title: 'Secure Digital Contracts',
        description:
          'Complete contracts electronically with financial and legal guarantees',
      },
      licensedProviders: {
        title: 'Licensed Service Providers',
        description:
          'All service providers are certified and licensed by relevant authorities',
      },
      continuousSupport: {
        title: 'Continuous Support',
        description:
          'A dedicated support team to assist you throughout all stages',
      },
    },
  },
  sectorsWeServe: {
    title: 'Sectors We Serve',
    sectors: {
      construction: 'Construction',
      sports: 'Sports',
      entertainment: 'Entertainment',
      government: 'Government',
      industrial: 'Industrial',
    },
  },
  howItWorks: {
    title: 'How Does the Platform Work?',
    steps: {
      register: {
        title: 'Register & Create Your Request',
        description:
          'Create your account and enter your request details including service type, location, and duration',
      },
      receiveOffers: {
        title: 'Receive Offers',
        description:
          'Qualified service providers will receive your request and you will get competitive quotes',
      },
      compareChoose: {
        title: 'Compare & Choose',
        description:
          'Review the offers, compare them, and choose the best one for your needs',
      },
      completeContract: {
        title: 'Complete the Contract',
        description:
          'Complete the contracting and payment process electronically in a secure and guaranteed manner',
      },
      receiveService: {
        title: 'Receive the Service',
        description:
          'The service provider executes the work and you can track and rate the service',
      },
    },
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'What is MedHub?',
        answer:
          'A digital platform connecting field medical service seekers with licensed providers',
      },
      {
        question: 'What types of services are available?',
        answer:
          'Ambulances, mobile clinics, medical teams, and medical equipment',
      },
      {
        question: 'How do I ensure the quality of service providers?',
        answer:
          'All service providers are licensed and you can view previous customer ratings',
      },
      {
        question: 'How long does it take to receive offers?',
        answer:
          'Usually 24 to 48 hours depending on the nature of the required service',
      },
      {
        question: 'Is the service available in all regions?',
        answer: 'Yes, the platform is available in all regions of Saudi Arabia',
      },
    ],
  },
  serviceProvider: {
    hero: {
      title: 'Service Providers Page',
      subtitle: 'Join MedHub — expand your business and get new opportunities',
      cta: 'Join Now',
    },
    whyJoin: {
      title: 'Why Join MedHub?',
      features: {
        fairCompetition: {
          title: 'Fair Competition',
          description:
            'A transparent bidding system that ensures equal opportunities',
        },
        newOpportunities: {
          title: 'New Job Opportunities',
          description:
            'Receive requests from companies and institutions without marketing efforts',
        },
        guaranteedPayments: {
          title: 'Guaranteed Payments',
          description:
            'A secure payment system that ensures you receive your dues',
        },
        easyManagement: {
          title: 'Easy Management',
          description: 'An integrated dashboard to manage everything',
        },
        professionalReputation: {
          title: 'Professional Reputation',
          description: 'Build your reputation through customer reviews',
        },
      },
    },
    howToStart: {
      title: 'How to Start as a Service Provider?',
      steps: [
        {
          title: 'Register Your Account',
          description:
            'Create a service provider account and complete your facility information',
        },
        {
          title: 'Attach Documents',
          description: 'Upload the required licenses and certificates',
        },
        {
          title: 'Wait for Activation',
          description:
            'Your documents will be reviewed and you will be notified upon activation',
        },
        {
          title: 'Receive Requests',
          description: 'Start receiving suitable customer requests',
        },
        {
          title: 'Submit Your Offers',
          description: 'Submit competitive quotes to win contracts',
        },
      ],
    },
    requiredDocuments: {
      title: 'Required Documents',
      items: [
        'Valid Commercial Registration',
        'Medical Activity Practice License',
        'Professional Insurance Certificate',
        'List of Licensed Medical Staff',
        'List of Equipment and Vehicles',
      ],
    },
  },
  footer: {
    tagline: 'Your Smart Platform for\nField Medical Services',
    quickLinks: 'Quick Links',
    contactUs: 'Contact Us',
    email: 'info@tgl.com.sa',
    phone: '+966 11 202 7444',
    copyright:
      '© {year} MedHub - Al Takassusi General Limited Company . All rights reserved',
    termsAndPrivacy: 'Terms & Privacy Policy',
  },
  languageSwitcher: {
    label: 'العربية',
  },
  seo: {
    home: {
      title:
        'MedHub — Smart Platform for Field Medical Services in Saudi Arabia',
      description:
        'A Saudi marketplace connecting customers with licensed field medical providers. Get quotes for ambulances, mobile clinics, and medical teams — compare and choose the best fit for your project or event.',
    },
    serviceProvider: {
      title: 'Join as a Medical Service Provider in Saudi Arabia | MedHub',
      description:
        'Join MedHub to grow your business — receive requests from companies and institutions needing licensed field medical services across Saudi Arabia.',
    },
  },
};
